import type { DiscoverSeason } from '../../api/compete'

export const DAY_MS = 1000 * 60 * 60 * 24
const HOUR_MS = 1000 * 60 * 60

export function formatDate(iso: string | null): string {
  if (!iso) return 'TBD'
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/**
 * When a competition starts. A tournament is one sitting, so it gets its start
 * time — "Dec 5" alone leaves a player guessing whether to show up at noon or 6.
 */
export function formatStart(iso: string | null, competitionType: DiscoverSeason['competition_type']): string {
  if (!iso) return 'TBD'
  if (competitionType !== 'tournament') return formatDate(iso)
  const d = new Date(iso)
  return `${formatDate(iso)} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`
}

export function formatPrize(cents: number): string {
  return `$${(cents / 100).toLocaleString()}`
}

export interface ClosesLabel {
  text: string
  /** Closing inside a week — rendered at full contrast rather than muted. */
  urgent: boolean
}

/**
 * How long is left to register, in the shortest form that is still precise.
 *
 * "22/32, closes Mar 2" made the reader do the arithmetic; the whole point of
 * this column is that urgency should be readable without it.
 */
export function closesLabel(iso: string | null, now: number = Date.now()): ClosesLabel {
  if (!iso) return { text: '—', urgent: false }
  const ms = new Date(iso).getTime() - now
  if (ms <= 0) return { text: 'Closed', urgent: false }
  const days = Math.floor(ms / DAY_MS)
  if (days < 1) {
    // Round up, so "59 minutes left" never renders as "0h left".
    const hours = Math.max(1, Math.floor(ms / HOUR_MS))
    return { text: `${hours}h left`, urgent: true }
  }
  if (days < 7) {
    const hours = Math.floor((ms % DAY_MS) / HOUR_MS)
    return { text: `${days}d ${hours}h left`, urgent: true }
  }
  return { text: formatDate(iso), urgent: false }
}

export interface EligibilityLabel {
  text: string
  /** This visitor cannot enter — the row dims, but stays readable. */
  blocked: boolean
}

/**
 * Rank range and Trust floor, answered for this visitor rather than stated as
 * a rule. "Trust 40+" is a rule; "you qualify" is an answer.
 */
export function eligibilityLabel(
  season: Pick<DiscoverSeason, 'min_trust_score' | 'min_rank_display' | 'max_rank_display'>,
  trustScore: number | null,
): EligibilityLabel {
  const floor = season.min_trust_score
  if (floor != null && floor > 0) {
    // Signed out, there is no "you" to answer for — state the rule.
    if (trustScore == null) return { text: `Trust ${floor}+`, blocked: false }
    if (trustScore < floor) {
      return { text: `Trust ${floor} — you're at ${trustScore}`, blocked: true }
    }
    return { text: `Trust ${floor} — you qualify`, blocked: false }
  }
  if (season.min_rank_display && season.max_rank_display) {
    return { text: `${season.min_rank_display} – ${season.max_rank_display}`, blocked: false }
  }
  const single = season.min_rank_display || season.max_rank_display
  return { text: single || 'Any rank', blocked: false }
}

/** The competition's own page on its organizer's site — "View competition". */
export function seasonHref(season: Pick<DiscoverSeason, 'org_slug' | 'slug'>): string {
  return `/org/${season.org_slug}/competitions/${season.slug}`
}

export type RegisterAction = 'register' | 'free-agent'

/**
 * Back to this page with a sign-up requested — where login sends a visitor who
 * picked "With a team" or "As a free agent" while signed out, so they finish
 * here instead of starting over.
 */
export function resumeSignUpHref(season: Pick<DiscoverSeason, 'id'>, action: RegisterAction): string {
  const q = new URLSearchParams({ register: season.id, as: action })
  return `/compete?${q}`
}

/** The sign-up `resumeSignUpHref` asked for, or null when the URL holds none. */
export function readResumeSignUp(params: URLSearchParams): { seasonId: string; action: RegisterAction } | null {
  const seasonId = params.get('register')
  const action = params.get('as')
  if (!seasonId || (action !== 'register' && action !== 'free-agent')) return null
  return { seasonId, action }
}

/** What signing up alone leads to, in the competition's own terms. */
export function freeAgentHint(mode: string): string {
  return mode === 'captain_recruit'
    ? 'Team captains can pick you up'
    : 'The organizers place you on a team'
}

/** What one entry is: a team, or for a solo game a player. */
export function entrantUnit(season: Pick<DiscoverSeason, 'team_size'>, count: number): string {
  const unit = season.team_size > 1 ? 'team' : 'player'
  return count === 1 ? unit : `${unit}s`
}

export interface FreeAgentRoom {
  /** Places for players in teams not yet entered: open team places × players per team. Null when teams are uncapped. */
  inOpenPlaces: number | null
  /** Team places still open, or null when uncapped. */
  openPlaces: number | null
  /** Empty starting places on entered teams that take free agents. */
  onTeams: number
  recruitingTeams: number
  /** Backup places on those teams: a count under a roster cap, or 'uncapped'. */
  bench: number | 'uncapped'
  /** Starting places a free agent could fill; null when it has no ceiling. */
  total: number | null
}

/**
 * Where a free agent could play, for a team game. Null for a solo game — there
 * is no team to join, so "free agent" means nothing there.
 *
 * Counts places, not promises: an open team place becomes a team only if a
 * captain or the organizers build one, which is why the page says "up to".
 */
export function freeAgentRoom(
  season: Pick<DiscoverSeason, 'team_size' | 'max_teams' | 'registered_teams' | 'max_roster_size'
    | 'recruiting_teams' | 'open_starting_spots' | 'open_bench_spots'>,
): FreeAgentRoom | null {
  if (season.team_size <= 1) return null
  const openPlaces = season.max_teams == null ? null : Math.max(0, season.max_teams - season.registered_teams)
  const inOpenPlaces = openPlaces == null ? null : openPlaces * season.team_size
  return {
    inOpenPlaces,
    openPlaces,
    onTeams: season.open_starting_spots,
    recruitingTeams: season.recruiting_teams,
    bench: season.max_roster_size > 0 ? season.open_bench_spots : 'uncapped',
    total: inOpenPlaces == null ? null : inOpenPlaces + season.open_starting_spots,
  }
}

/**
 * Urgency is the order that matters on this page: a season closing tonight
 * outranks one closing in a month, whatever order the API returned.
 * Seasons with no deadline sort last rather than first.
 */
export function sortByClosing<T extends Pick<DiscoverSeason, 'registration_closes_at'>>(
  seasons: T[],
): T[] {
  const far = Number.MAX_SAFE_INTEGER
  return [...seasons].sort((a, b) => {
    const at = a.registration_closes_at ? new Date(a.registration_closes_at).getTime() : far
    const bt = b.registration_closes_at ? new Date(b.registration_closes_at).getTime() : far
    return at - bt
  })
}

/**
 * Who gets the spotlight, from a list already in display order.
 *
 * Every competition Carousel is featuring that is still visible under the
 * current filters, highest priority first — so picking Leagues or Tournaments
 * pages through that type's picks. Equal priorities keep the list's order
 * (soonest closing). With nothing featured, the first row takes it alone, as it
 * always did. `rest` is everything else, for the list below.
 */
export function pickSpotlights<T extends Pick<DiscoverSeason, 'featured_priority'>>(
  sorted: T[],
): { spotlights: T[]; rest: T[]; featured: boolean } {
  const featured = sorted
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.featured_priority != null)
    .sort((a, b) => (b.s.featured_priority as number) - (a.s.featured_priority as number) || a.i - b.i)
    .map(({ s }) => s)
  if (featured.length > 0) {
    const set = new Set(featured)
    return { spotlights: featured, rest: sorted.filter((s) => !set.has(s)), featured: true }
  }
  return { spotlights: sorted.slice(0, 1), rest: sorted.slice(1), featured: false }
}

/** How many of these close inside the next week — the hero's urgency figure. */
export function countClosingThisWeek(
  seasons: Pick<DiscoverSeason, 'registration_closes_at'>[],
  now: number = Date.now(),
): number {
  return seasons.filter((s) => {
    if (!s.registration_closes_at) return false
    const ms = new Date(s.registration_closes_at).getTime() - now
    return ms > 0 && ms < 7 * DAY_MS
  }).length
}

/**
 * How many competitions this visitor's Trust Score currently locks them out
 * of. `scoreFor` gives their score as each competition judges it (per game,
 * where per-game eligibility is on) — null when unknown, which never counts.
 */
export function countBlocked<T extends Pick<DiscoverSeason, 'min_trust_score'>>(
  seasons: T[],
  scoreFor: (season: T) => number | null,
): number {
  return seasons.filter((s) => {
    const score = scoreFor(s)
    return score != null && s.min_trust_score != null && score < s.min_trust_score
  }).length
}

/**
 * The fewest tips, biggest first, that together close the gap to a minimum —
 * the ones worth doing first. Empty when there is no gap. If every tip
 * together still falls short, all of them: each one helps.
 */
export function tipsToReach<T extends { points: number }>(tips: T[], gap: number): T[] {
  if (gap <= 0) return []
  const picked: T[] = []
  let total = 0
  for (const tip of [...tips].sort((a, b) => b.points - a.points)) {
    if (total >= gap) break
    if (tip.points <= 0) continue
    picked.push(tip)
    total += tip.points
  }
  return picked
}
